/**
 * Consumer-facing explainer for DRG-bundle inpatient pricing.
 *
 * Many hospitals — especially smaller/rural ones — publish a single bundled price
 * for an inpatient stay (a "DRG" rate) instead of itemizing every billing code.
 * When the selected hospital priced THIS scenario that way (its scenario coverage
 * row carries pricing_method 'drg'), we show this card so the patient understands
 * why the number is built differently — at a plain reading level.
 *
 * Self-contained: it reads the selected hospital's coverage row (for the DRG flag
 * + bundle total) from scenarioCoverageStore, and the scenario's DRG family/title
 * from scenarios_index.json — so it works regardless of how the parent loaded the
 * scenario. Renders nothing unless the hospital is DRG-priced for this scenario.
 */
import React, { useEffect, useState } from "react";
import { Info, Layers } from "lucide-react";
import { loadStaticData } from "@/lib/offlineData";
import { loadScenarioCoverageFor } from "@/lib/scenarioCoverageStore";

interface DrgInfo {
  baseDrg: string;
  baseDrgTitle?: string | null;
  drgFamily?: string[];
}

interface Props {
  scenarioId?: string;
  selectedHospitalId?: string | null;
  hospitalName?: string;
  dark?: boolean;
  /** The hospital's actual matched DRG row (code + published bundle price), when the
   *  parent has fetched it. Without it we fall back to the family's base DRG label
   *  and the blended coverage total — which once showed "DRG 476 / $651,685" for a
   *  hospital whose real published bundle was DRG 474 at $634,153. */
  bundleRow?: { code: string; price: number } | null;
}

// Tidy a CMS DRG title ("807 - VAGINAL DELIVERY WITHOUT STERILIZATION...") into
// something readable: drop the leading number, sentence-case.
function prettyDrgTitle(title?: string | null): string | null {
  if (!title) return null;
  const t = title.replace(/^\s*\d+\s*[-–]\s*/, "").trim();
  if (!t) return null;
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}

const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;

export default function DrgBundleExplainer({ scenarioId, selectedHospitalId, hospitalName, dark, bundleRow }: Props) {
  const [bundlePrice, setBundlePrice] = useState<number | null>(null);
  const [isDrg, setIsDrg] = useState(false);
  const [drg, setDrg] = useState<DrgInfo | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!scenarioId || !selectedHospitalId) { setIsDrg(false); return; }
    (async () => {
      try {
        const rows = await loadScenarioCoverageFor(scenarioId);
        const row = rows.find((r: any) => r.h === selectedHospitalId);
        if (cancelled) return;
        if (row && row.pm === "drg") {
          setIsDrg(true);
          setBundlePrice(typeof row.hp === "number" ? row.hp : null);
        } else {
          setIsDrg(false);
          return;
        }
        // DRG family/title from the scenario index (cached after first load).
        const idx = await loadStaticData<{ data: any[] }>("scenarios_index.json");
        if (cancelled) return;
        const meta = idx?.data?.find((s: any) => s.id === scenarioId || s.uuid === scenarioId);
        if (meta?.drgInpatient) {
          setDrg({
            baseDrg: meta.drgInpatient.baseDrg,
            baseDrgTitle: meta.drgInpatient.baseDrgTitle,
            drgFamily: meta.drgInpatient.drgFamily,
          });
        }
      } catch {
        if (!cancelled) setIsDrg(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scenarioId, selectedHospitalId]);

  if (!isDrg) return null;

  // Dark: a brighter steel-blue so heading/link/pill read clearly instead of
  // low-contrast blue-on-navy against the near-black page.
  const navy = dark ? "#9ec5ef" : "var(--civic-navy)";
  const cardBg = dark ? "rgba(var(--civic-navy-soft-rgb),0.08)" : "rgba(var(--civic-navy-rgb),0.05)";
  const border = dark ? "rgba(158,197,239,0.28)" : "rgba(var(--civic-navy-rgb),0.18)";
  const text = dark ? "var(--civic-warm)" : "var(--civic-ink)";
  const muted = dark ? "rgba(var(--civic-warm-rgb),0.62)" : "rgba(var(--civic-ink-rgb),0.6)";
  const drgLabel = bundleRow?.code ? `MS-DRG ${bundleRow.code}` : (drg ? `DRG ${drg.baseDrg}` : "a DRG bundle");
  const titleClean = prettyDrgTitle(drg?.baseDrgTitle);

  return (
    <div className="rounded-xl p-3 mb-3" style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}>
      <div className="flex items-start gap-2.5">
        <div className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: dark ? "rgba(var(--civic-navy-soft-rgb),0.18)" : "rgba(var(--civic-navy-rgb),0.1)" }}>
          <Layers className="w-4 h-4" style={{ color: navy }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span style={{ fontSize: 13, fontWeight: 700, color: navy, fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>
              Inpatient stay priced as one bundle
            </span>
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: dark ? "rgba(var(--civic-navy-soft-rgb),0.18)" : "rgba(var(--civic-navy-rgb),0.08)", color: navy }}>
              {drgLabel}
            </span>
          </div>
          <p style={{ fontSize: 12.5, color: muted, lineHeight: 1.5, marginTop: 4 }}>
            {hospitalName ? hospitalName : "This hospital"} lists the hospital part of this care as a single{" "}
            <button onClick={() => setOpen((o) => !o)} className="underline decoration-dotted underline-offset-2" style={{ color: navy }}>
              all-in “DRG” price
            </button>
            {bundleRow?.price ? (
              <> (<span style={{ fontWeight: 700, color: text }}>{fmt(bundleRow.price)}</span> from its published file{bundlePrice != null && bundlePrice > bundleRow.price ? <>; {fmt(bundlePrice)} total with outpatient add-ons</> : null})</>
            ) : bundlePrice != null ? (
              <> (<span style={{ fontWeight: 700, color: text }}>{fmt(bundlePrice)}</span> for the stay)</>
            ) : null}
            {" "}instead of itemizing each code. Outpatient visits (like follow-up care) are added on top.
          </p>
          {open && (
            <div className="mt-2 rounded-lg p-2.5" style={{ backgroundColor: dark ? "rgba(0,0,0,0.25)" : "rgba(255,255,255,0.7)", border: `1px solid ${border}` }}>
              <div className="flex items-start gap-2">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: navy }} />
                <p style={{ fontSize: 11.5, color: muted, lineHeight: 1.55 }}>
                  A <strong style={{ color: text }}>DRG</strong> (Diagnosis-Related Group) is the bundled, all-in amount
                  Medicare and many hospitals use for a whole inpatient stay{titleClean ? <> — here, <em>{titleClean}</em></> : null}.
                  It already covers the room, procedure, and supplies for the stay as one number, so it can’t be split into
                  individual line items. We compare it against the national average for the same DRG bundle, and add any
                  outpatient costs separately so the total is complete.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
